import { useEffect, useState } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { isProductEnabled } from "@/business/product-access";
import { ROLE_OPTIONS, roleLabel, USER_ROLES } from "@/user/constants";
import { roleAccessDescription } from "@/user/role-access-description";

interface RoleSelectorProps {
  value: string;
  originalRole?: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
  exclude?: string[];
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
  productSettings?: unknown;
}

export const RoleSelector = ({
  value,
  originalRole = value,
  onChange,
  label = "Role",
  placeholder = "Select a role",
  fullWidth = false,
  required = false,
  exclude = [],
  disabled = false,
  disabledTooltip,
  productSettings,
}: RoleSelectorProps) => {
  const [roleSearch, setRoleSearch] = useState("");

  useEffect(() => {
    setRoleSearch(roleLabel(value));
  }, [value]);

  const accessDescription = roleAccessDescription(value, productSettings);
  const apEnabled = isProductEnabled(productSettings, "accounts_payable");
  const options =
    originalRole === USER_ROLES.MANAGER || originalRole === USER_ROLES.REP
      ? [
          ...ROLE_OPTIONS,
          { value: originalRole, label: roleLabel(originalRole) },
        ]
      : ROLE_OPTIONS;

  const renderRoleOption = (option: { value: string; label: string }) => (
    <Stack align="start" gap={2}>
      <Text className="font-medium">{option.label}</Text>
      <Text variant="caption" tone="muted">
        {roleAccessDescription(option.value, productSettings)}
      </Text>
    </Stack>
  );

  return (
    <Stack className={cn({ "w-full": fullWidth })} align="start" gap={2}>
      <AutoComplete
        label={label}
        selectedValue={value}
        onSelectedValueChange={onChange}
        searchValue={roleSearch}
        onSearchValueChange={setRoleSearch}
        items={options.filter(
          (option) =>
            !exclude.includes(option.value) &&
            (option.value !== USER_ROLES.AP_ONLY ||
              apEnabled ||
              originalRole === USER_ROLES.AP_ONLY) &&
            option.label.toLowerCase().includes(roleSearch.toLowerCase()),
        )}
        renderOption={renderRoleOption}
        scrollAreaClassName="max-h-96 in-data-[slot=dialog-content]:max-h-64"
        emptyMessage="No roles found"
        placeholder={placeholder}
        required={required}
        fullWidth={fullWidth}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
      />
      {accessDescription && (
        <Text variant="caption" tone="muted">
          {accessDescription}
        </Text>
      )}
    </Stack>
  );
};
